import React, { memo, useMemo } from 'react';
import { useWindowSize } from 'react-use';
import { NearbyStation } from 'types/windStations';
import { Speed } from 'types/units';
import { orderNorthToSouth } from 'utils/geo/latLonOrdering';
import { DesktopPremiumWindStationCTA } from 'components/PremiumWindStationCTA';
import WindStationCard from 'components/WindStationCard';
import config from '../../config';
import WindStationsVirtualList from './WindStationsVirtualList';
import styles from './MapWindStationCards.module.scss';

export const generateWindStationsToDisplay = (
  windStations: NearbyStation[] | undefined,
  showPaywall: boolean,
) => {
  const sortedWindStations = orderNorthToSouth(windStations);
  if (showPaywall) {
    // Display a maximum of 4 stations
    const windStationsSlice = sortedWindStations?.slice(0, 4);

    // Do not display actual data
    const windStationsToDisplay: NearbyStation[] = [];
    windStationsSlice?.forEach((windStation, index) => {
      windStationsToDisplay[index] = {
        ...windStationsSlice[index],
        lastObserved: {
          ...windStationsSlice[index].lastObserved,
          wind: { direction: 0, speed: 0, gust: 0 },
          timestamp: Number(windStation.lastObserved?.timestamp),
          utcOffset: Number(windStation.lastObserved?.utcOffset),
        },
      };
    });

    return windStationsToDisplay;
  }
  return sortedWindStations;
};

interface Props {
  windStations: NearbyStation[] | undefined;
  windSpeedUnits: Speed;
  hasLiveWindPermissions: boolean;
  segmentProperties: {
    pageName: string;
  };
}

const MapWindStationCards: React.FC<Props> = ({
  windStations,
  windSpeedUnits,
  hasLiveWindPermissions,
  segmentProperties,
}) => {
  const { width: deviceWidth } = useWindowSize();
  const isVerticalView = deviceWidth > config.tabletLargeWidth;

  const showPaywall = !hasLiveWindPermissions;

  const windStationsToDisplay: NearbyStation[] | undefined = useMemo(
    () => generateWindStationsToDisplay(windStations, showPaywall),
    [showPaywall, windStations],
  );

  const paywallWindStationData: NearbyStation = {
    _id: 'paywallWindStation',
    lastObserved: {
      timestamp: Math.floor(Date.now() / 1000),
      wind: {
        speed: 5,
        direction: 45,
        gust: 7,
      },
      utcOffset: -1,
    },
    location: {
      lat: 10,
      lon: 10,
    },
    stationName: 'Wind Station',
    provider: 'DAVIS',
  };

  const paywallWindStation = (
    <WindStationCard
      windStation={paywallWindStationData}
      windSpeedUnit={windSpeedUnits}
      isLoading={false}
      showPaywall={false}
      segmentProperties={segmentProperties}
    />
  );

  return (
    <>
      <ul
        data-testid="map-wind-station-cards"
        className={styles.mapWindStationCards}
        aria-label="List of wind stations within the map boundaries"
      >
        <WindStationsVirtualList
          isVerticalView={isVerticalView}
          windStations={windStationsToDisplay}
          windSpeedUnits={windSpeedUnits}
          showPaywall={showPaywall}
          segmentProperties={segmentProperties}
        />
      </ul>
      {showPaywall && isVerticalView && (
        <div className={styles.paywallContainer}>
          <DesktopPremiumWindStationCTA
            windStationCard={paywallWindStation}
            segmentProperties={segmentProperties}
          />
        </div>
      )}
    </>
  );
};

export default memo(MapWindStationCards);
